// S2 (v0.13.0) — TenantSwitcher.
//
// Only renders for super admins. Drives the tenant scope used by the
// API client interceptor — selecting "Platform View" clears the
// scope so calls go un-scoped (suited to /api/v1/admin/*); selecting
// a tenant attaches ?tenant_id=<id> to every subsequent request.
//
// P5 restyle slice 1 — replaced the native <select> (wouldn't open in
// Safari: height-squeezed inside the h-10 header bar) with the shadcn
// Select popover. Behavior is unchanged; only the presentation differs.
import { useQueryClient } from "@tanstack/react-query";

import { useAdminTenants } from "@/api/adminTenants";
import { useTenantStore } from "@/store/tenant";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Radix Select disallows empty-string item values, so "Platform View"
// (store value: currentTenantId === null) uses this sentinel internally.
// It never leaves this component — onValueChange maps it back to null.
const PLATFORM = "__platform__";

export function TenantSwitcher() {
  const isSuperAdmin = useTenantStore((s) => s.isSuperAdmin);
  const currentTenantId = useTenantStore((s) => s.currentTenantId);
  const setTenant = useTenantStore((s) => s.setTenant);
  const { data: tenants = [] } = useAdminTenants();
  const qc = useQueryClient();

  if (!isSuperAdmin) return null;

  return (
    <Select
      value={currentTenantId ?? PLATFORM}
      onValueChange={(value) => {
        if (value === PLATFORM) {
          setTenant(null, "Platform View", "");
        } else {
          const t = tenants.find((x) => x.id === value);
          setTenant(value, t?.name ?? "", t?.plan ?? "");
        }
        // Re-scope every cached query to the new tenant. Query keys don't
        // include the tenant, so without this the cache would keep serving
        // the previous tenant's data. resetQueries() clears the prior
        // tenant's cached data AND refetches active queries — so the new
        // ?tenant_id (appended lazily by the api/client interceptor) takes
        // effect immediately. One invalidation point covers all scoped
        // screens, for both selecting a tenant and "Platform View".
        void qc.resetQueries();
      }}
    >
      <SelectTrigger
        title="Tenant scope"
        // Trigger reads on the dark #1b2a3b bar: light text, subtle border.
        className="border-white/20 bg-white/10 text-white hover:bg-white/20"
      >
        <SelectValue placeholder="Platform View" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={PLATFORM}>Platform View</SelectItem>
        {tenants.map((t) => (
          <SelectItem key={t.id} value={t.id}>
            {t.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
